import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1583525068955-0fc7f1f83cfb?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SecurityAspectsContainer = styled.div`
  margin: 4rem 0;
`;

const SecurityAspect = styled.div`
  background-color: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const AspectNumber = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #0d9488;
  color: white;
  font-weight: bold;
  font-size: 1.5rem;
  margin-right: 1rem;
`;

const AspectTitle = styled.h3`
  display: inline-block;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #0d9488;
  font-style: italic;
`;

const AspectDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #e0e0e0;
  margin-top: 1rem;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const SecurityTools = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 4rem 0;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ToolCard = styled.div`
  background-color: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const ToolIcon = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  
  svg {
    width: 100%;
    height: 100%;
    color: #0d9488;
  }
`;

const ToolTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #0d9488;
  font-style: italic;
`;

const ToolDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin: 4rem 0 2rem;
  text-align: center;
  color: #0d9488;
  font-style: italic;
`;

const Security: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const securityAspects = [
    {
      number: 1,
      title: language === 'it' ? 'PULIZIA REGOLARE' : 'REGULAR CLEANING',
      description: language === 'it' 
        ? 'La pulizia è essenziale per garantire che la proprietà sia presentabile e igienica ad ogni nuovo arrivo di ospiti. Programmare servizi di pulizia regolari è fondamentale per mantenere uno standard elevato.'
        : 'Cleaning is essential to ensure that the property is presentable and hygienic with each new guest arrival. Scheduling regular cleaning services is essential to maintaining a high standard.'
    },
    {
      number: 2,
      title: language === 'it' ? 'CONTROLLO E RIPARAZIONE DI ARREDI E ELETTRODOMESTICI' : 'CHECK AND REPAIR OF FURNISHINGS AND APPLIANCES',
      description: language === 'it' 
        ? 'Controllare periodicamente le condizioni degli arredi e degli elettrodomestici è importante per identificare eventuali malfunzionamenti o danni. Riparare o sostituire gli elementi danneggiati tempestivamente aiuta a mantenere la qualità della proprietà.'
        : 'Periodically checking the condition of the furnishings and appliances is important to identify any malfunctions or damage. Repairing or replacing damaged items promptly helps maintain the quality of the property.'
    },
    {
      number: 3,
      title: language === 'it' ? 'ISPEZIONE DEGLI IMPIANTI' : 'FACILITIES INSPECTION',
      description: language === 'it' 
        ? 'Controllare regolarmente le strutture dell\'immobile, inclusi tetto, pavimenti, pareti e impianti idraulici ed elettrici, per identificare e risolvere problemi di manutenzione preventiva.'
        : 'Regularly check property structures, including roof, floors, walls, and plumbing and electrical systems, to identify and resolve preventative maintenance issues.'
    },
    {
      number: 4,
      title: language === 'it' ? 'FORNITURA DI BENI DI CONSUMO' : 'SUPPLY OF CONSUMABLES',
      description: language === 'it' 
        ? 'Assicurarsi che i beni di consumo essenziali come carta igienica, asciugamani, lenzuola pulite e altri articoli necessari per un soggiorno confortevole siano disponibili. Mantenere un inventario regolare di questi articoli è importante.'
        : 'Make sure essential consumables such as toilet paper, towels, clean sheets and other items necessary for a comfortable stay are available. Maintaining a regular inventory of these items is important.'
    },
    {
      number: 5,
      title: language === 'it' ? 'GESTIONE DI GIARDINI E SPAZI ESTERNI' : 'GARDEN AND OUTDOOR SPACE MANAGEMENT',
      description: language === 'it' 
        ? 'Se l\'immobile dispone di spazi esterni, come giardini o cortili, è importante mantenerli in buone condizioni. Questo può includere la potatura delle piante, la pulizia degli spazi esterni e la gestione di eventuali problematiche legate all\'ambiente esterno.'
        : 'If the property has outdoor spaces, such as gardens or courtyards, it is important to keep them in good condition. This may include pruning plants, cleaning outdoor spaces and managing any issues related to the outdoor environment.'
    },
    {
      number: 6,
      title: language === 'it' ? 'ASSISTENZA TECNICA RAPIDA' : 'QUICK TECHNICAL ASSISTANCE',
      description: language === 'it' 
        ? 'Avere un sistema efficiente per gestire le segnalazioni di problemi da parte degli ospiti e rispondere prontamente a qualsiasi richiesta di assistenza tecnica è essenziale per garantire la soddisfazione degli ospiti.'
        : 'Having an efficient system to manage problem reports from guests and respond promptly to any requests for technical assistance is essential to ensure guest satisfaction.'
    },
    {
      number: 7,
      title: language === 'it' ? 'CONFORMITÀ LEGALE E SICUREZZA' : 'LEGAL COMPLIANCE AND SAFETY',
      description: language === 'it' 
        ? 'Mantenere la tua proprietà in conformità con le normative locali è essenziale per evitare problemi legali. Questo può includere il controllo delle normative di sicurezza, l\'installazione di dispositivi di sicurezza e il rispetto delle leggi sugli affitti temporanei.'
        : 'Maintaining your property in compliance with local regulations is essential to avoid legal problems. This may include checking safety regulations, installing safety devices and complying with temporary rental laws.'
    }
  ];
  
  const securityTools = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
        </svg>
      ),
      title: language === 'it' ? 'SISTEMI DI SICUREZZA' : 'SECURITY SYSTEMS',
      description: language === 'it' 
        ? 'Installa sistemi di allarme, serrature intelligenti e videocamere di sicurezza per proteggere la proprietà e garantire la tranquillità degli ospiti.'
        : 'Install alarm systems, smart locks and security cameras to protect the property and ensure guest peace of mind.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
        </svg>
      ),
      title: language === 'it' ? 'SERRATURE INTELLIGENTI' : 'SMART LOCKS',
      description: language === 'it' 
        ? 'Utilizza serrature intelligenti con codici temporanei per un accesso sicuro e controllato alla proprietà senza la necessità di scambio fisico di chiavi.'
        : 'Use smart locks with temporary codes for secure and controlled access to the property without the need for physical key exchange.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 8.71l-5.83-5.83a2 2 0 0 0-2.83 0L5.83 8.71c-.39.39-.39 1.02 0 1.41l8.49 8.49c.39.39 1.02.39 1.41 0l3.83-3.83a.996.996 0 0 0 0-1.41l-2.34-2.34a.996.996 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83a3 3 0 0 0 0-4.24l-2.34-2.34a3 3 0 0 0-4.24 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path>
        </svg>
      ),
      title: language === 'it' ? 'DISPOSITIVI ANTINCENDIO' : 'FIRE PROTECTION DEVICES',
      description: language === 'it' 
        ? 'Installa rilevatori di fumo, estintori e coperte antincendio, e assicurati che siano regolarmente controllati e mantenuti in buone condizioni.'
        : 'Install smoke detectors, fire extinguishers and fire blankets, and make sure they are regularly checked and kept in good condition.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Sicurezza | J-O-H' : 'Security | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Servizi di sicurezza e manutenzione per immobili in affitto breve, garantendo tranquillità a proprietari e ospiti' 
            : 'Security and maintenance services for short-term rental properties, ensuring peace of mind for owners and guests'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'SICUREZZA' : 'SECURITY'}</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'La manutenzione degli immobili per affitti brevi è un aspetto cruciale per garantire che le proprietà rimangano attraenti, funzionali e sicure per gli ospiti. La natura temporanea degli affitti brevi richiede un\'attenzione particolare alla manutenzione per garantire esperienze positive agli ospiti e redditività del business.'
              : 'The maintenance of short-term rental properties is a crucial aspect of ensuring that properties remain attractive, functional and safe for guests. The temporary nature of short-term rentals requires careful attention to maintenance to ensure positive guest experiences and business profitability.'}
          </Description>
          
          <SecurityAspectsContainer>
            {securityAspects.map((aspect) => (
              <SecurityAspect key={aspect.number}>
                <AspectNumber>{aspect.number}</AspectNumber>
                <AspectTitle>{aspect.title}</AspectTitle>
                <AspectDescription>{aspect.description}</AspectDescription>
              </SecurityAspect>
            ))}
          </SecurityAspectsContainer>
          
          <SectionTitle>
            {language === 'it' ? 'STRUMENTI DI SICUREZZA' : 'SECURITY TOOLS'}
          </SectionTitle>
          
          <SecurityTools>
            {securityTools.map((tool, index) => (
              <ToolCard key={index}>
                <ToolIcon>
                  {tool.icon}
                </ToolIcon>
                <ToolTitle>{tool.title}</ToolTitle>
                <ToolDescription>{tool.description}</ToolDescription>
              </ToolCard>
            ))}
          </SecurityTools>
          
          <Description>
            {language === 'it' 
              ? 'Una gestione robusta della manutenzione per gli immobili in affitto breve aiuta a garantire un\'esperienza positiva per gli ospiti, a preservare il valore della proprietà e a minimizzare i rischi legali. La manutenzione regolare è un investimento che paga dividendi nel mantenere una reputazione positiva e nell\'attrarre futuri ospiti.'
              : 'Robust maintenance management for short-term rental properties helps ensure a positive guest experience, preserve property value and minimize legal risks. Regular maintenance is an investment that pays dividends in maintaining a positive reputation and attracting future guests.'}
          </Description>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci per i Servizi di Sicurezza' : 'Contact Us for Security Services'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Security; 